import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Controller, useForm } from 'react-hook-form'
import { BpInput } from '../../../../shared/components/forms/input/BpInput'
import { IconButton } from '../../../../shared/components/buttons/icon-button/IconButton'
import { IconTrash } from '../../../../shared/components/icons/IconTrash'
import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { BpCheckbox } from '../../../../shared/components/forms/checkbox/BpCheckbox'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'
import { BpAccordion } from '../../../../shared/components/utility/accordion/BpAccordion'
import {
  TrackingParamConfig,
  useGetFilterDomainZonesQuery,
  useGetProjectBrandDomainsQuery,
  usePostAffiliateRuleCreateCustomZoneMutation,
  usePostAffiliateRuleCreateMutation,
} from '../../../../app/api'
import { hideModal, setModal, showAlert } from '../../../../app/store/reducers/AppSlice'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { AddParameter } from './AddParameter'
import { BpTextArea } from '../../../../shared/components/forms/textarea/BpTextArea'
import { useParams } from 'react-router-dom'
import { IOption } from '../../../../shared/components/forms/search-input/BpSearchInput'
import { URL_PROTOCOL_REGEX, URL_REGEX } from '../../../../shared/consts/RegularExpressions.const'
import { Tooltip } from '../../../../shared/components/text/tooltip/Tooltip'
import { extractDomainZone } from '../../../../shared/lib/helpers/ExtractDomainZone'
import { extractPathSegments } from '../../../../shared/lib/helpers/TokenizePathUrl'
import { getUrlHostname } from '../../../../shared/lib/helpers/GetUrlHostname'
import { renderPathBasedRoles, renderPathBasedUrl } from './lib/PathBasedUrl'
import { AddDomain } from './AddDomain'
import { IconPlus } from '../../../../shared/components/icons/IconPlus'
import { clearUrl } from '../../../../shared/lib/helpers/ClearUrl'
import { Trans, useTranslation } from 'react-i18next'
import { selectUser } from '../../../../app/store/reducers/AuthSlice'
import clsx from 'clsx'
import styles from './CreateAffiliateRule.module.scss'

export interface CreateAffiliateRuleRef {
  hasUnsavedChanges: () => boolean
}

interface IUrlParam {
  key: string
  value?: string
  showIgnore: boolean
  showTrack: boolean
}

interface IFormValues {
  url: string
  params: Record<
    string,
    { type: TrackingParamConfig['type']; ignored_values?: string; allowed_values?: string }
  >
  domainZones: string[]
}

type FormFieldPath = keyof IFormValues | `params.${string}` | `domainZones.${number}`

const useUrlParams = (
  getValues: () => IFormValues,
  setValue: (name: FormFieldPath, value: any) => void,
  isPathBased: boolean,
) => {
  const parseUrlParams = useCallback(
    (url: string, pathBased: boolean = isPathBased): IUrlParam[] => {
      if (pathBased) {
        return extractPathSegments(url).map((value) => ({
          key: value,
          showIgnore: false,
          showTrack: false,
        }))
      }

      try {
        const urlObj = new URL(url)
        const searchParams = new URLSearchParams(urlObj.search)
        const paramsArray: IUrlParam[] = []

        searchParams.forEach((value, key) => {
          paramsArray.push({ key, value, showIgnore: false, showTrack: false })
        })

        return paramsArray
      } catch {
        return []
      }
    },
    [isPathBased],
  )

  const updateUrlWithParam = useCallback(
    (paramKey: string) => {
      const currentUrl = getValues().url
      if (!currentUrl) return

      try {
        const urlObj = new URL(currentUrl)
        const searchParams = new URLSearchParams(urlObj.search)
        searchParams.append(paramKey, '')
        urlObj.search = searchParams.toString()
        setValue('url', urlObj.toString())
      } catch {
        // If URL is invalid, do nothing
      }
    },
    [getValues, setValue],
  )

  const removeParamFromUrl = useCallback(
    (paramKey: string) => {
      const currentUrl = getValues().url
      if (!currentUrl) return

      const urlObj = new URL(currentUrl)
      const searchParams = new URLSearchParams(urlObj.search)

      searchParams.delete(paramKey)
      urlObj.search = searchParams.toString()
      setValue('url', urlObj.toString())
    },
    [getValues, setValue],
  )

  const cleanupParams = useCallback(
    (newParams: IUrlParam[]) => {
      const currentValues = getValues()
      if (currentValues.params) {
        const newParamsKeys = new Set(newParams.map((p) => p.key))
        const updatedParams = Object.entries(currentValues.params).reduce<Record<string, any>>(
          (acc, [key, value]) => {
            if (newParamsKeys.has(key)) {
              acc[key] = value
            }
            return acc
          },
          {},
        )
        setValue('params', updatedParams)
      }
    },
    [getValues, setValue],
  )

  return {
    parseUrlParams,
    updateUrlWithParam,
    removeParamFromUrl,
    cleanupParams,
  }
}

export const CreateAffiliateRule = forwardRef<CreateAffiliateRuleRef>((_, ref) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const [opened, setOpened] = useState(false)
  const [step, setStep] = useState(1)
  const [params, setParams] = useState<IUrlParam[]>([])
  const [isPathBased, setIsPathBased] = useState(false)
  const [disabled, setDisabled] = useState(true)
  const user = useAppSelector(selectUser)
  const dispatch = useAppDispatch()

  const ROLE_OPTIONS = useMemo(
    () => [
      { value: 'affiliate', text: t('pages.project.affiliateParameter') },
      { value: 'network', text: t('pages.project.networkParameter') },
      { value: 'other', text: t('pages.project.otherParameter') },
    ],
    [t],
  )

  const sortedParams = useMemo(() => {
    const seen = new Set<string>()
    const dedupeAndCap = (list: IUrlParam[]) =>
      list
        .filter((p) => {
          const key = p.key.toLowerCase()
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
        .slice(0, 10)

    if (isPathBased) {
      return dedupeAndCap(params)
    }

    const pinnedKeys = ['utm_source', 'sv_campaign_id', 'campaign_id', 'utm_campaign']
    const pinnedParams = params.filter((p) => pinnedKeys.includes(p.key.toLowerCase()))
    const otherParams = params.filter((p) => !pinnedKeys.includes(p.key.toLowerCase()))

    return dedupeAndCap([...pinnedParams, ...otherParams])
  }, [params, isPathBased])

  const form = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: {
      url: '',
      params: {},
      domainZones: [],
    },
  })

  const {
    register,
    watch,
    setValue,
    reset,
    getValues,
    trigger,
    formState: { errors, isDirty, isValid },
  } = form

  const formValues = watch()
  const { parseUrlParams, updateUrlWithParam, removeParamFromUrl, cleanupParams } = useUrlParams(
    getValues,
    setValue,
    isPathBased,
  )

  const didMountRef = useRef(false)

  const handleTogglePathBased = useCallback(() => {
    const next = !isPathBased
    const newParams = parseUrlParams(getValues().url, next)

    setIsPathBased(next)
    setParams(newParams)
    cleanupParams(newParams)
  }, [isPathBased, parseUrlParams, getValues, cleanupParams])

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }

    trigger('url')
  }, [isPathBased, trigger])

  const renderCampaignExamples = () =>
    isPathBased ? (
      <>
        <p className={styles.helpBlockP}>
          {t('pages.project.containsText')}:
          <br />
          https://brand.com/inhouse_brand_campaign_id/..., {t('pages.project.enter')}{' '}
          <span className={styles.helpBlockBold}>inhouse_brand_campaign_id</span>
          <br />
          https://brand.com/#trusted_affiliate_id, {t('pages.project.enter')}{' '}
          <span className={styles.helpBlockBold}>trusted_affiliate_id</span>
        </p>
        <p className={styles.helpBlockP}>
          ✅ {t('other.correct')}: trusted_affiliate_id
          <br />❌ {t('other.incorrect')}: #trusted_affiliate_id/
          <br />❌ {t('other.incorrect')}: https://brand.com/#trusted_affiliate_id
        </p>
      </>
    ) : (
      <>
        <p className={styles.helpBlockP}>
          {t('pages.project.containsText')}:
          <br />
          utm_campaign=google_ads, {t('pages.project.enter')}{' '}
          <span className={styles.helpBlockBold}>google_ads</span>
          <br />
          utm_source=trusted_affiliate, {t('pages.project.enter')}{' '}
          <span className={styles.helpBlockBold}>trusted_affiliate</span>
        </p>
        <p className={styles.helpBlockP}>
          ✅ {t('other.correct')}: google_ads
          <br />❌ {t('other.incorrect')}: utm_campaign=google_ads
          <br />❌ {t('other.incorrect')}: https://example.com/?utm_campaign=google_ads
        </p>
      </>
    )

  const [createAffiliateRule] = usePostAffiliateRuleCreateMutation()
  const [createCustomZone] = usePostAffiliateRuleCreateCustomZoneMutation()
  const { data: projectDomains } = useGetProjectBrandDomainsQuery(
    {
      projectId: Number(id),
      url: clearUrl(formValues.url),
    },
    {
      skip:
        !id ||
        !formValues.url ||
        !URL_REGEX.test(formValues.url) ||
        !parseUrlParams(formValues.url).length,
    },
  )
  const { data: domainZones, refetch: refetchDomainZones } = useGetFilterDomainZonesQuery()

  const transformDomainZones = useMemo(() => {
    const currentDomain = extractDomainZone(formValues.url)

    return (
      domainZones?.map((zone) => ({
        ...zone,
        allowDelete: zone.value !== currentDomain,
      })) || []
    )
  }, [domainZones, formValues.url])

  const filteredDomains =
    projectDomains?.data?.filter(
      (domain: string) => !formValues.domainZones?.includes(extractDomainZone(domain)),
    ) || []

  const handleAddParam = useCallback(
    (paramKey: string) => {
      setParams((prev) => [...prev, { key: paramKey, showIgnore: false, showTrack: false }])
      updateUrlWithParam(paramKey)
    },
    [updateUrlWithParam],
  )

  const handleRemoveParam = useCallback(
    (paramKey: string) => {
      setParams((prev) => prev.filter((p) => p.key !== paramKey))
      removeParamFromUrl(paramKey)

      const values = getValues()
      const { [paramKey]: _removed, ...restParams } = values.params || {}

      reset({
        ...values,
        params: restParams,
      })
    },
    [getValues, reset, removeParamFromUrl],
  )

  const handleAddDomain = useCallback(
    (domain: string) => {
      const zone = domain.slice(domain.lastIndexOf('.'))

      if (zone) {
        const newZones = Array.from(new Set([...(formValues.domainZones || []), zone]))

        setValue('domainZones', newZones)
      }
    },
    [formValues.domainZones, setValue],
  )

  const toggleIgnore = useCallback((paramKey: string) => {
    setParams((prev) =>
      prev.map((p) => (p.key === paramKey ? { ...p, showIgnore: true, showTrack: false } : p)),
    )
  }, [])

  const toggleTrack = useCallback((paramKey: string) => {
    setParams((prev) =>
      prev.map((p) => (p.key === paramKey ? { ...p, showTrack: true, showIgnore: false } : p)),
    )
  }, [])

  useImperativeHandle(ref, () => ({
    hasUnsavedChanges: () => isDirty,
  }))

  const submit = (additional?: { run_sync?: boolean }) => {
    switch (step) {
      case 1: {
        const currentDomain = extractDomainZone(
          formValues.url,
          domainZones?.map((zone) => zone.value),
        )

        if (currentDomain) {
          setValue('domainZones', [currentDomain])
        }

        setStep(step + 1)
        break
      }
      case 2: {
        if (
          !Object.entries(formValues.params).filter(
            ([_, value]: any) => value?.type === 'affiliate',
          ).length
        ) {
          dispatch(
            showAlert({
              message: t('pages.project.mustAssignedAffiliate'),
              timeout: 10000,
              type: 'error',
            }),
          )
        } else {
          setStep(step + 1)
        }
        break
      }
      case 3: {
        setStep(step + 1)
        break
      }
      case 4: {
        const result = {
          scenario: 'create' as const,
          project_id: Number(id),
          url: formValues.url,
          domain_zones: formValues.domainZones,
          is_path_based: isPathBased,
          enabled: true,
          run_sync: !!additional?.run_sync,
          params: Object.entries(formValues.params)
            .filter(([key, value]: any) => !!value?.type)
            .map(([key, value]) => ({
              value: key,
              type: value.type,
              ignored_values: value.ignored_values?.length ? value.ignored_values?.split('\n') : [],
              allowed_values: value.allowed_values?.length ? value.allowed_values?.split('\n') : [],
            })),
        }

        createAffiliateRule({ body: result }).then((response) => {
          if ('error' in response || !response.data.success) {
            return
          }

          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: 'aff_rule_created',
            properties: {
              user_id: user.id,
              company_id: user.company_id,
              project_id: id,
            },
          })

          dispatch(hideModal())
          dispatch(
            showAlert({
              message: additional?.run_sync
                ? t('pages.project.affiliatesBeingRecreated')
                : t('pages.project.affiliateRuleCreated'),
              type: 'success',
              timeout: 2000,
            }),
          )
        })
        break
      }
    }
  }

  useEffect(() => {
    const first = !isValid || !params.length
    const second =
      formValues.params &&
      (!params.length ||
        !Object.entries(formValues.params).filter(([_, value]: any) => value?.type === 'affiliate')
          .length)
    const third = !formValues?.domainZones?.length

    setDisabled({ 1: first, 2: second, 3: third }[step] || false)
  }, [formValues, isValid, step])

  useEffect(() => {
    setOpened(false)
  }, [step])

  return (
    <div className={styles.root}>
      <p className={styles.stepLabel}>Step {step} of 4</p>
      <div>
        <div className={clsx(styles.stepPanel, step !== 1 && styles.stepPanelHidden)}>
          <h3 className={styles.title}>{t('pages.project.addSampleLink')}</h3>
          <div className={styles.step1Body}>
            <div className={'flex flex-col gap-4'}>
              <BpInput
                required
                error={errors.url?.message as string}
                placeholder={t('pages.project.placeholders.trackingLink')}
                value={formValues.url}
                onClear={() => setValue('url', '', { shouldValidate: true })}
                {...register('url', {
                  required: t('fields.validate.required'),
                  validate: {
                    hasProtocol: (value) =>
                      URL_PROTOCOL_REGEX.test(value) || t('pages.project.validate.startWithHttp'),
                    isValidUrl: (value) =>
                      URL_REGEX.test(value) || t('pages.project.validate.invalidUrl'),
                    hasParams: (value) =>
                      !!parseUrlParams(value, isPathBased).length ||
                      t('pages.project.validate.noParametersFound'),
                  },
                  onChange: (e) => {
                    const url = e.target.value.trim()
                    const newParams = parseUrlParams(url)

                    setValue('url', url)

                    setParams(newParams)
                    cleanupParams(newParams)
                  },
                })}
              />
              <div className='flex items-center gap-[8px] px-1'>
                <BpCheckbox
                  checked={isPathBased}
                  label={t('pages.project.pathBasedTrackingLabel')}
                  onChange={handleTogglePathBased}
                />
                <BpTooltip
                  title={
                    <div className={'flex flex-col gap-2'}>
                      <p>{t('pages.project.pathBasedTrackingHint')}</p>
                      <ul>
                        <li className={'whitespace-nowrap'}>
                          https://brand-domain.com/
                          <span className={'text-[#18EDB0]'}>affiliate_Id</span>/
                          <span className={'text-[#18EDB0]'}>network_id</span>
                        </li>
                        <li className={'whitespace-nowrap'}>
                          https://brand-domain.com/#
                          <span className={'text-[#18EDB0]'}>affiliate_id</span>
                        </li>
                      </ul>
                    </div>
                  }
                  overlayClassName={'!max-w-[350px] !w-[350px]'}
                />
              </div>
              <p className={'whitespace-pre-line px-1 text-[13px] text-aqua-300'}>
                {t('pages.project.trackingLinkInfo')}
              </p>
            </div>
            <div>
              <p className={styles.whereLink}>
                {t('pages.project.whereDoGetIt')}{' '}
                <span className={styles.linkToggle} onClick={() => setOpened((o) => !o)}>
                  {opened ? t('pages.project.showLess') : t('pages.project.showMore')}
                </span>
              </p>
              <BpAccordion opened={opened}>
                <p className={styles.accordionText}>{t('pages.project.tipRedirectedLink')}</p>
                <p className={styles.accordionTextFirst}>{t('pages.project.tipAskDirectLink')}</p>
                <p className={styles.accordionText}>{t('pages.project.examples')}</p>
                <ul className={styles.accordionList}>
                  <li>https://brand-domain.com/?utm_source=awin&utm_campaign=affiliate1</li>
                  <li>https://brand-domain.com/?utm_source=cpc&utm_campaign=spring_sale</li>
                </ul>
              </BpAccordion>
            </div>
          </div>
        </div>

        <div className={clsx(styles.stepPanel, step !== 2 && styles.stepPanelHidden)}>
          <h3 className={styles.title}>{t('pages.project.selectParameters')}</h3>
          <div className={styles.step2Scroll}>
            <p className={styles.assignText}>
              {isPathBased ? (
                <Trans i18nKey='pages.project.assignRolesPath'>
                  <span className={styles.fontWeight600} />
                </Trans>
              ) : (
                <>
                  {t('pages.project.assignRoles')}{' '}
                  <Trans i18nKey='pages.project.roleExplanation'>
                    <span className={styles.fontWeight600} />
                  </Trans>
                </>
              )}
            </p>
            <div className={styles.tipsBlock}>
              <p className={styles.accordionTextFirst}>
                💡{t('pages.project.importantTips')}{' '}
                <span className={styles.tipsToggle} onClick={() => setOpened((o) => !o)}>
                  {opened ? t('buttons.close') : t('buttons.show')}
                </span>
              </p>
              <BpAccordion opened={opened}>
                {isPathBased ? (
                  <ul className={styles.tipsList}>
                    <li>
                      <p>{t('pages.project.tipStaticValuesPath')}</p>
                    </li>
                    <li>
                      <p className={styles.tipsListItem}>
                        {t('pages.project.tipMatchStructurePath')}
                      </p>
                    </li>
                    <li>
                      <p className={styles.tipsListItem}>
                        {t('pages.project.tipSeparateRulePath')}
                      </p>
                    </li>
                  </ul>
                ) : (
                  <>
                    <ul className={styles.tipsList}>
                      <li>
                        <p>{t('pages.project.tipStableParams')}</p>
                      </li>
                      <li>
                        <p className={styles.tipsListItem}>
                          {t('pages.project.tipAvoidChangingParams')}
                        </p>
                      </li>
                      <li>
                        <p className={styles.tipsListItem}>
                          {t('pages.project.tipAllParamsMustMatch')}
                        </p>
                      </li>
                    </ul>
                    <div className={styles.tipsNested}>
                      <p className={styles.tipsListItem}>
                        {t('pages.project.tipDifferentCombinations')}
                      </p>
                      <ul className={styles.tipsDiscList}>
                        <li>utm_source + utm_campaign</li>
                        <li>utm_source + sv_utm_campaign</li>
                      </ul>
                      <p className={styles.tipsListItem}>
                        {t('pages.project.tipCreateSeparateRules')}
                      </p>
                    </div>
                  </>
                )}
              </BpAccordion>
            </div>
            <div className={styles.paramsBody}>
              {sortedParams.map((param) => {
                const typeValue = watch(`params.${param.key}.type`)

                return (
                  <div key={param.key} className={styles.paramCard}>
                    <div className={styles.paramCardInner}>
                      <div className={styles.paramHeader}>
                        <p className={styles.paramKey}>
                          {isPathBased ? (
                            <Tooltip
                              title={
                                <span className={'break-all'}>
                                  {renderPathBasedUrl(formValues.url, param.key, false)}
                                </span>
                              }
                              placement={'topLeft'}
                            >
                              <span>
                                {renderPathBasedUrl(
                                  formValues.url,
                                  param.key,
                                  formValues.url.length > 50,
                                )}
                              </span>
                            </Tooltip>
                          ) : (
                            <>
                              {param.key}
                              {param.value ? (
                                <>
                                  =<span className={styles.paramValue}>{param.value}</span>
                                </>
                              ) : (
                                ''
                              )}
                            </>
                          )}
                        </p>
                        <IconButton size='lg' onClick={() => handleRemoveParam(param.key)}>
                          <IconTrash color='#00326D' />
                        </IconButton>
                      </div>
                      <div className={styles.paramFormRow}>
                        <Controller
                          key={`params.${param.key}.type`}
                          render={({ field: { onChange, ...fieldProps } }) => (
                            <BpSelect
                              dropPosition={'auto'}
                              {...fieldProps}
                              className={styles.selectWidth}
                              fieldSize='sm'
                              options={ROLE_OPTIONS}
                              placeholder={t('pages.project.selectRole')}
                              onChange={(e) => {
                                onChange(e)

                                if (e.target.value.length) {
                                  return
                                }

                                if (param.showIgnore) {
                                  setValue(`params.${param.key}.ignored_values`, '')
                                }

                                if (param.showTrack) {
                                  setValue(`params.${param.key}.allowed_values`, '')
                                }
                              }}
                            />
                          )}
                          control={form.control}
                          name={`params.${param.key}.type`}
                        />
                        <div className={styles.paramActions}>
                          <span
                            className={clsx(
                              styles.paramAction,
                              param.showIgnore ? styles.paramActionAqua : styles.paramActionFocus,
                            )}
                            hidden={!typeValue?.length}
                            onClick={() => toggleIgnore(param.key)}
                          >
                            {t('pages.project.ignoreCampaigns')}
                          </span>
                          <span
                            className={clsx(
                              styles.paramAction,
                              param.showTrack ? styles.paramActionAqua : styles.paramActionFocus,
                            )}
                            hidden={!typeValue?.length}
                            onClick={() => toggleTrack(param.key)}
                          >
                            {t('pages.project.specificCampaigns')}
                          </span>
                        </div>
                      </div>
                      {!!typeValue?.length &&
                        (param.showIgnore ? (
                          <div key={`params.${param.key}.ignored_values`}>
                            <BpTextArea
                              {...register(`params.${param.key}.ignored_values`, {
                                onChange: (e) => {
                                  setValue(
                                    `params.${param.key}.ignored_values`,
                                    e.target.value.replace(',', '\n'),
                                  )
                                },
                                onBlur: (e) => {
                                  setValue(
                                    `params.${param.key}.ignored_values`,
                                    e.target.value
                                      .split('\n')
                                      .map((value: string) => value.trim())
                                      .join('\n'),
                                  )
                                },
                              })}
                              label={t('pages.project.excludeCampaigns')}
                              placeholder={'campaign1\ncampaign2\ncampaign3'}
                            />
                            <div className={styles.helpBlock}>
                              {renderCampaignExamples()}
                              <p>{t('pages.project.ignoreValuesNote')}</p>
                            </div>
                          </div>
                        ) : (
                          param.showTrack && (
                            <div key={`params.${param.key}.allowed_values`}>
                              <BpTextArea
                                {...register(`params.${param.key}.allowed_values`, {
                                  onChange: (e) => {
                                    setValue(
                                      `params.${param.key}.allowed_values`,
                                      e.target.value.replace(',', '\n'),
                                    )
                                  },
                                  onBlur: (e) => {
                                    setValue(
                                      `params.${param.key}.allowed_values`,
                                      e.target.value
                                        .split('\n')
                                        .map((value: string) => value.trim())
                                        .join('\n'),
                                    )
                                  },
                                })}
                                label={t('pages.project.trackCampaigns')}
                                placeholder={'campaign1\ncampaign2\ncampaign3'}
                              />
                              <div className={styles.helpBlock}>
                                {renderCampaignExamples()}
                                <p>
                                  {isPathBased
                                    ? t('pages.project.trackValuesNotePath')
                                    : t('pages.project.trackValuesNote')}
                                </p>
                              </div>
                            </div>
                          )
                        ))}
                    </div>
                  </div>
                )
              })}
              {!isPathBased && (
                <BpButton
                  className={styles.addParamButton}
                  contentClassName={styles.addParamContent}
                  size={'lg'}
                  theme='transparent'
                  onClick={() =>
                    dispatch(
                      setModal({
                        size: 'md',
                        children: (
                          <AddParameter
                            currentParameters={params.map((value) => value.key)}
                            onSubmit={handleAddParam}
                          />
                        ),
                      }),
                    )
                  }
                >
                  + {t('pages.project.addParameter')}
                </BpButton>
              )}
            </div>
          </div>
        </div>

        <div className={clsx(styles.stepPanel, step !== 3 && styles.stepPanelHidden)}>
          <h3 className={styles.title}>{t('pages.project.selectDomainsTitle')}</h3>
          <div className={styles.step3Body}>
            <div>
              <BpSelect
                multiple
                bottomSection={
                  <button
                    className={styles.addDomainButton}
                    type={'button'}
                    onClick={() =>
                      dispatch(
                        setModal({
                          size: 'md',
                          children: (
                            <AddDomain
                              currentDomains={formValues.domainZones}
                              domains={domainZones?.map((zone) => zone.value)}
                              onSubmit={(value) => {
                                const domain = extractDomainZone(
                                  value,
                                  domainZones?.map((zone) => zone.value),
                                )

                                if (transformDomainZones.find(({ value }) => domain === value)) {
                                  setValue('domainZones', [...formValues.domainZones, domain])
                                } else {
                                  createCustomZone({
                                    body: { project_id: Number(id), url: value },
                                  }).then((response) => {
                                    const customZone = Object.keys((response as any)?.data.data)[0]

                                    refetchDomainZones().then(() => {
                                      setValue('domainZones', [
                                        ...formValues.domainZones,
                                        customZone,
                                      ])
                                    })
                                  })
                                }
                              }}
                            />
                          ),
                        }),
                      )
                    }
                  >
                    {t('pages.project.addCustomDomain')}
                    <div className={styles.addDomainIconWrap}>
                      <IconPlus color={'#A059FF'} />
                    </div>
                  </button>
                }
                dropPosition={'auto'}
                options={transformDomainZones}
                placeholder={t('pages.project.selectDomains')}
                value={formValues.domainZones}
                {...register('domainZones', {
                  onChange: (e) => {
                    setValue(
                      'domainZones',
                      [].slice.call(e.target.selectedOptions).map((o: IOption) => o.value),
                    )
                  },
                })}
              />
              {filteredDomains.length > 0 && (
                <p className={styles.domainHint}>
                  <span className={styles.domainHintLabel}>{t('pages.project.addDomains')}: </span>
                  {filteredDomains.map((domain: string, index: number) => (
                    <>
                      <span
                        key={index}
                        className={styles.domainLink}
                        onClick={() => handleAddDomain(domain)}
                      >
                        {domain}
                      </span>
                      {index !== filteredDomains.length - 1 ? ', ' : ''}
                    </>
                  ))}
                </p>
              )}
              <p className={styles.tldNote}>{t('pages.project.tldNote')}</p>
            </div>
          </div>
        </div>

        <div className={clsx(styles.stepPanel, step !== 4 && styles.stepPanelHidden)}>
          <h3 className={styles.title}>{t('pages.project.reviewAndCreate')}</h3>
          <div className={styles.step4Body}>
            <div className={styles.reviewCard}>
              <div className={styles.paramActions}>
                <p className={styles.reviewLabel}>{t('pages.project.url')}:</p>
                <p className={clsx(styles.reviewValue, 'break-all')}>{formValues.url}</p>
              </div>
            </div>
            <div className={styles.reviewCardRow}>
              <div className={styles.paramActions}>
                <p className={styles.reviewLabel}>{t('pages.project.parameters')}:</p>
                <div>
                  {isPathBased ? (
                    <span className={clsx(styles.reviewInline, 'break-all')}>
                      {renderPathBasedRoles(
                        formValues.url,
                        (segment) => formValues.params?.[segment]?.type,
                      )}
                    </span>
                  ) : (
                    params
                      .filter((p) => watch(`params.${p.key}.type`))
                      .map((p, i, filtered) => (
                        <span key={p.key} className={styles.reviewInline}>
                          {p.key}
                          {p.value ? `=${p.value}` : ''}
                          {i < filtered.length - 1 ? ', ' : ''}
                        </span>
                      ))
                  )}
                </div>
              </div>
              <BpButton
                className={styles.editButton}
                size='sm'
                theme='outline'
                type='button'
                onClick={() => setStep(2)}
              >
                {t('buttons.edit')}
              </BpButton>
            </div>
            <div className={styles.reviewCardRow}>
              <div className={styles.paramActions}>
                <p className={styles.reviewLabel}>{t('pages.project.domains')}:</p>
                {isPathBased ? (
                  <Tooltip placement={'topLeft'} title={formValues.url}>
                    <p className={'w-fit break-all'}>{getUrlHostname(formValues.url)}</p>
                  </Tooltip>
                ) : (
                  <p>{formValues.domainZones?.join(', ')}</p>
                )}
              </div>
              <BpButton
                className={styles.editButton}
                size='sm'
                theme='outline'
                type='button'
                onClick={() => setStep(3)}
              >
                {t('buttons.edit')}
              </BpButton>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        {step > 1 && (
          <BpButton
            contentClassName={styles.backContent}
            size='xs'
            theme='transparent'
            type='button'
            onClick={() => setStep(step - 1)}
          >
            {t('buttons.back')}
          </BpButton>
        )}
        <Tooltip
          title={
            disabled &&
            step === 1 && (
              <span className={styles.whitespaceNowrap}>{t('pages.project.fillAllFields')}</span>
            )
          }
          placement={'topRight'}
        >
          <div className={styles.footerActions}>
            {step === 4 && (
              <BpButton
                className={styles.nextButton}
                disabled={disabled}
                theme={'outline'}
                type='button'
                onClick={() => submit({ run_sync: true })}
              >
                {t('pages.project.createUpdateRule')}
              </BpButton>
            )}
            <Tooltip
              title={
                step === 2 &&
                (!params.length ||
                  !Object.entries(formValues.params).filter(
                    ([_, value]: any) => value?.type === 'affiliate',
                  ).length) &&
                'At least one must be Affiliate'
              }
            >
              <div>
                <BpButton
                  className={styles.nextButton}
                  disabled={disabled}
                  type='button'
                  onClick={() => submit()}
                >
                  {step !== 4 ? t('buttons.next') : t('buttons.createRule')}
                </BpButton>
              </div>
            </Tooltip>
          </div>
        </Tooltip>
      </div>
    </div>
  )
})
